/**
 * Maps frontend hall form/normalized state to HallMaster API + DB field structure.
 */

export const MAX_HALL_IMAGES = 12;

export function flatFormToHallShape(form) {
  const businessTypes = Array.isArray(form.business_types) && form.business_types.length
    ? form.business_types
    : (form.business_type ? [form.business_type] : ['marriage_hall']);

  return {
    name: form.name,
    code: form.code,
    description: form.description,
    capacity: form.capacity,
    manager_name: form.manager_name,
    phone: form.phone,
    email: form.email,
    business_types: businessTypes,
    business_type: businessTypes[0] || 'marriage_hall',
    banquet_style_capacity: form.banquet_style_capacity,
    theatre_style_capacity: form.theatre_style_capacity,
    dining_capacity: form.dining_capacity,
    floor_area: form.floor_area,
    floor: form.floor,
    is_24_hours: Boolean(form.is_24_hours),
    open_time: form.is_24_hours ? '00:00' : (form.open_time || '09:00'),
    close_time: form.is_24_hours ? '23:59' : (form.close_time || '23:00'),
    pricing: {
      hourly_rate: form.hourly_rate,
      full_day_rate: form.full_day_rate,
    },
    hall_image_files: Array.isArray(form.hall_image_files) ? form.hall_image_files : [],
    removed_image_ids: Array.isArray(form.removed_image_ids) ? form.removed_image_ids : [],
    status: 'available',
    is_active: true,
  };
}

export function buildHallApiPayload(hall) {
  const description = hall.description === 'No description added yet.' ? '' : (hall.description || '');
  const businessTypes = Array.isArray(hall.business_types) && hall.business_types.length
    ? hall.business_types
    : (hall.business_type ? [hall.business_type] : ['marriage_hall']);
  const is24Hours = Boolean(hall.is_24_hours || hall.operating_hours?.is_24_hours);

  return {
    name: String(hall.name || '').trim(),
    code: String(hall.code || hall.hall_code || '').trim(),
    description,
    capacity: Number(hall.capacity || 0),
    contact_info: {
      manager_name: hall.manager_name || '',
      phone: hall.phone || '',
      email: hall.email || '',
    },
    business_info: {
      business_types: businessTypes,
    },
    layout_info: {
      banquet_style_capacity: Number(hall.banquet_style_capacity || 0),
      theatre_style_capacity: Number(hall.theatre_style_capacity || 0),
      dining_capacity: Number(hall.dining_capacity || 0),
      floor_area: hall.floor_area || '',
      floor: hall.floor || '',
      dimensions: hall.dimensions || '',
      minimum_guests: Number(hall.min_guests || 0),
      maximum_guests: Number(hall.max_guests || hall.capacity || 0),
    },
    pricing_info: {
      hourly_rate: Number(hall.pricing?.hourly_rate || 0),
      full_day_rate: Number(hall.pricing?.full_day_rate || 0),
    },
    working_hours: {
      is_24_hours: is24Hours,
      open_time: is24Hours ? '00:00' : (hall.operating_hours?.open || hall.open_time || '09:00'),
      close_time: is24Hours ? '23:59' : (hall.operating_hours?.close || hall.close_time || '23:00'),
    },
    status: hall.status || 'available',
    is_active: (hall.status === 'inactive' || hall.is_active === false) ? false : true,
  };
}

export function buildHallFormData(hall, imageFiles = null) {
  const payload = buildHallApiPayload(hall);
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value ?? '');
    }
  });

  const pendingFiles = Array.isArray(imageFiles)
    ? imageFiles
    : (imageFiles ? [imageFiles] : (Array.isArray(hall.hall_image_files) ? hall.hall_image_files : []));

  pendingFiles.forEach((file) => {
    if (file) formData.append('hall_images', file);
  });

  const removedIds = Array.isArray(hall.removed_image_ids) ? hall.removed_image_ids : [];
  if (removedIds.length) {
    formData.append('remove_image_ids', JSON.stringify(removedIds));
  }

  return formData;
}
